import React, { useEffect, useImperativeHandle, useState } from "react";

import DataTable, { CellSize } from "../../Components/Table/table";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { assetService, cacheService, thumbnailService } from "../../Core/Api/services";
import {
    GetArtworkMeta,
    GetBroadcasterList,
    GetCulturesList,
    HandleChangeArtworkMetadata,
    RemoveArtwork,
} from "../../Redux/Reducers/redux-slice-bundle-maker";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import { ModalContext } from "../../Components/Modals";
import ModalEditArtworkMetadata, {
    EditArtworkMetadataProps,
} from "../../Components/Modals/ModalEditArtworkMetadata/modal-edit-artwork-metadata";
import Viewer from "react-viewer";
import { ThumbnailSize } from "../../Core/Api/Thumbnail/thumbnail-api-service";
import ModalConfirmAction, {
    ModalConfirmActionElementProps,
} from "../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import { useDispatch } from "react-redux";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { Box, Typography } from "@mui/material";
import PosterImagePack from "../../Components/Misc/PosterImagePack";
import { useTranslation } from "react-i18next";
interface ArtworksTableProps {
    select: boolean;
    preSelectedIds?: number[];
    showActions: boolean;
}
enum ArtworkPosterSubTypes {
    ArtworkPosterHorizontal = "Horizontal",
    ArtworkPosterVertical = "Vertical",
}

export interface SelectedRows {
    getSelectedRows: () => number[];
    handleSetSelectedRows: (artworkIds: number[]) => void;
}
export type artworkTableRef = React.ElementRef<typeof ArtworksTable>;

const ArtworksTable = React.forwardRef<SelectedRows | null | undefined, ArtworksTableProps>((props, ref) => {
    const { t } = useTranslation();
    const { select, preSelectedIds, showActions } = props;
    const rows = useSelector(GetArtworkMeta) || [];
    const cultures = useSelector(GetCulturesList);
    const broadcasters = useSelector(GetBroadcasterList);
    const [posterViewerVisible, setPosterViewerVisible] = useState<number | undefined>(undefined);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const dispatch = useDispatch();

    useEffect(() => {
        preSelectedIds && setSelectedRows(preSelectedIds);
    }, [preSelectedIds?.length]);

    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };

    const getSelectedRows = () => selectedRows;

    useImperativeHandle(ref, () => ({
        getSelectedRows: getSelectedRows,
        handleSetSelectedRows(artworkIds) {
            setSelectedRows(artworkIds);
        },
    }));

    const enqueueCustomSnackbar = useCustomSnackbar();

    const onClose: EditArtworkMetadataProps["onClose"] = (data) => {
        enqueueCustomSnackbar(t("alert:success.artworkMetadataUpdated"), "success");
        dispatch(HandleChangeArtworkMetadata({ artworkId: data.artworkId, metadata: data.metadata }));
    };

    const handleRadio = (e) => {
        e.target.checked
            ? setSelectedRows((x) => [...x, e.target.value])
            : setSelectedRows((x) => x.filter((v) => v !== e.target.value));
    };

    const handleEditArtwork = (data: any) => {
        openModal &&
            openModal<EditArtworkMetadataProps>(ModalEditArtworkMetadata, {
                data,
                onClose,
            });
    };

    const handleDeleteArtwork = (id: number) => {
        if (!openModal) return;

        openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
            displayComment: false,
            title: t("modal:title.deleteArtwork"),
            onConfirm: () => {
                assetService.DeleteObjectById(id).then((res) => {
                    if (res.success) {
                        enqueueCustomSnackbar(t("alert:success.artworkDeleted"), "success");
                        dispatch(RemoveArtwork(id));
                    } else {
                        enqueueCustomSnackbar(res.message || t("alert:error.deletingArtwork"), "error");
                    }
                });
            },
        });
    };

    const columns: GridColDef[] = [
        {
            field: "thumbnail",
            headerName: t("common:thumbnailLowerCase"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => (
                <PosterImagePack
                    imgs={[
                        {
                            src: `${thumbnailService.GetServiceUrl()}/thumbs/reference/${cacheService.GetAuthKey()}/${data?.row?.id}`,
                        },
                    ]}
                    posterProps={{
                        width: 50,
                        height: 39,
                        borderRadius: "2px",
                    }}
                />
            ),
        },
        {
            field: "countries",
            headerName: t("table:headerName.countries"),
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            valueGetter: (params: any, row: any) =>
                row?.metadata
                    ?.find((x) => cacheService.GetMetadata().find((m) => m.tagName === "artworkCultureIds")?.id === x.id)
                    ?.value?.map((x) => cultures.find((c) => c.id === x)?.englishName || "")
                    .join(", ") || "",
        },
        {
            field: "platform",
            headerName: t("common:platform"),
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            valueGetter: (params: any, row: any) => {
                const metadataValue = row?.metadata?.find(
                    (x) => cacheService.GetMetadata().find((m) => m.tagName === "broadcasterId")?.id === x.id
                )?.value;

                return metadataValue ? broadcasters.find((b) => b.id === metadataValue)?.name || "" : "";
            },
        },
        {
            field: "format",
            headerName: t("common:format"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            valueGetter: (params: any, row: any) =>
                row?.metadata?.find((x) => cacheService.GetMetadata().find((m) => m.tagName === "techCodec")?.id === x.id)
                    ?.value || "",
        },
        {
            field: "content sub type",
            headerName: t("field:label.orientation"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            valueGetter: (params: any, row: any) =>
                ArtworkPosterSubTypes[
                row?.metadata?.find(
                    (x) => cacheService.GetMetadata().find((m) => m.tagName === "contentSubType")?.id === x.id
                )?.value
                ] || "",
        },
        {
            field: "language",
            headerName: t("common:language"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            valueGetter: (params: any, row: any) =>
                row?.metadata?.find(
                    (x) =>
                        cacheService.GetMetadata().find((m) => m.tagName === "languagePicture" && m.layer === ObjectLayer.Asset)
                            ?.id === x.id
                )?.value?.label || "",
        },
        {
            field: "actions",
            type: "actions",
            maxWidth: showActions ? 120 : 0,
            align: "right",
            getActions: (params: GridRowParams) =>
                showActions && params
                    ? [
                        <GridActionsCellItem
                            icon={<VisibilityIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                            label={t("common:preview")}
                            onClick={() => {
                                setPosterViewerVisible(params?.row?.id);
                            }}
                        />,
                        <GridActionsCellItem
                            label={t("common:editUpperCase")}
                            onClick={() => handleEditArtwork(params.row)}
                            icon={<ModeEditOutlinedIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                        />,
                        <GridActionsCellItem
                            label={t("common:delete")}
                            onClick={() => handleDeleteArtwork(params?.row?.id)}
                            icon={<DeleteOutlineIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                        />,
                    ]
                    : [],
        },
    ];

    return (
        <>
            {rows.length > 0 ? (
                <DataTable
                    noControls
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    columns={columns}
                    rows={rows}
                />
            ) : (
                <Box m={2} display="flex" justifyContent="center" width="100%">
                    <Typography variant="h6">{t("page:bdy.noArtworksFoundForThisTitle")}</Typography>
                </Box>
            )}
            <Viewer
                visible={posterViewerVisible !== undefined}
                noNavbar={true}
                zIndex={2001}
                onClose={() => {
                    setPosterViewerVisible(undefined);
                }}
                images={[
                    {
                        src: thumbnailService.GetObjectPosterUrl(posterViewerVisible, ThumbnailSize.original),
                        alt: "Image viewer #" + posterViewerVisible + " ",
                    },
                ]}
            />
        </>
    );
});

export default ArtworksTable;
